import React, { useState, useEffect } from "react";
import useUserDetails from "../../utils/hooks/user/useUserDetails";
import Toggle from "../forms/inputs/Toggle";
import { useUpdateUserNotificationsMutation } from "../../utils/api/endpoints/usersApi";
import { useSelector } from "react-redux";
import { RootState } from "../../utils/store/store";
import toast from "react-hot-toast";
import { UserRole } from "../../utils/api/endpoints/user/credentials";

// Re-usable Icons
const ReportIcon = () => (
  <div className="bg-[#58CC02]/[0.17] p-2 rounded-lg">
    <svg className="w-6 h-6 text-[#58CC02]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  </div>
);

const ProgramIcon = () => (
  <div className="bg-[#58CC02]/[0.17] p-2 rounded-lg">
    <svg className="w-6 h-6 text-[#58CC02]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  </div>
);

const UserIcon = () => (
  <div className="bg-[#58CC02]/[0.17] p-2 rounded-lg">
    <svg className="w-6 h-6 text-[#58CC02]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  </div>
);

const MailIcon = () => (
  <div className="bg-[#58CC02]/[0.17] p-2 rounded-lg">
    <svg className="w-5 h-5 text-[#58CC02]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  </div>
);

const CommentIcon = () => (
  <div className="bg-[#58CC02]/[0.17] p-2 rounded-lg">
    <svg className="w-5 h-5 text-[#58CC02]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
    </svg>
  </div>
);

const SeverityDropdown = ({ selected, onChange, disabled }: { selected: any, onChange: any, disabled?: boolean }) => {
  const [isOpen, setIsOpen] = useState(false);
  const severities = ["Critical", "High", "Medium", "Low", "Info"];
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className={`relative mr-4 ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 border rounded bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50"
        disabled={disabled}
      >
        <span className="w-2 h-2 rounded-full bg-red-500"></span>
        {selectedCount === 5 ? "All Severities" : selectedCount === 0 ? "None" : `Severities (${selectedCount})`}
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-[100] mt-1 right-0 w-40 bg-white border rounded-lg shadow-xl py-1">
          {severities.map(s => (
            <label key={s} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer text-xs transition-colors">
              <input
                type="checkbox"
                checked={!!selected[s.toLowerCase()]}
                onChange={(e) => onChange(s.toLowerCase(), e.target.checked)}
                className="w-3.5 h-3.5 rounded text-ctb-primary border-gray-300 focus:ring-ctb-primary"
              />
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${s === "Critical" ? "bg-red-500" :
                    s === "High" ? "bg-orange-500" :
                      s === "Medium" ? "bg-yellow-500" :
                        s === "Low" ? "bg-blue-500" :
                          "bg-gray-400"
                  }`}></span>
                <span className="text-gray-700 font-medium">{s}</span>
              </div>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const PreferenceSection = ({ title, description, icon, enabled, onToggle, isGlobalOff, children }: any) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const isActuallyEnabled = enabled && !isGlobalOff;

  return (
    <div className={`bg-white border rounded-xl shadow-sm transition-opacity ${isGlobalOff ? "opacity-60 grayscale-[0.5] pointer-events-none" : ""}`}>
      <div className="p-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {icon}
          <div>
            <h3 className="text-base font-bold text-gray-900">{title}</h3>
            <p className="text-xs text-gray-500">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="scale-110">
            <Toggle value={isActuallyEnabled} onChange={onToggle} disabled={isGlobalOff} activeTrackColor="#0D1117" />
          </div>
          <button onClick={() => setIsExpanded(!isExpanded)} className="text-gray-400">
            <svg className={`w-5 h-5 transition-transform ${isExpanded ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
            </svg>
          </button>
        </div>
      </div>
      {isExpanded && isActuallyEnabled && (
        <div className="border-t">
          {children}
        </div>
      )}
    </div>
  );
};

const PreferenceRow = ({ label, title, description, icon, enabled, onToggle, extra, isGlobalOff }: any) => (
  <div className="p-6 flex items-center justify-between hover:bg-gray-50/50 transition-colors border-b last:border-b-0 last:rounded-b-xl">
    <div className="w-1/4 text-xs font-semibold text-ctb-primary flex items-center gap-2">
      <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
      {label}
    </div>
    <div className="w-1/2 flex items-center gap-4">
      {icon}
      <div>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        <p className="text-xs text-gray-500 max-w-md leading-relaxed">{description}</p>
      </div>
    </div>
    <div className="w-1/4 flex items-center justify-end">
      {extra}
      <div className="scale-90">
        <Toggle value={enabled && !isGlobalOff} onChange={onToggle} disabled={isGlobalOff} activeTrackColor="#0D1117" />
      </div>
    </div>
  </div>
);

const NotificationSettings = ({ className }: { className?: string }) => {
  const { user, setDetails } = useUserDetails();
  const { email_preferences, username, email_notifications_enabled } = user;
  const userRole = useSelector((state: RootState) => state.user.credentials.userRole);
  const [prefs, setPrefs] = useState<any>(null);

  useEffect(() => {
    // Standard system defaults
    const defaults = {
      reports: {
        enabled: true,
        vulnerability_reports: {
          enabled: true,
          severities: { critical: true, high: true, medium: true, low: true, info: true }
        },
        comments: { enabled: true },
        retests: { enabled: true }
      },
      programs: {
        enabled: true,
        invitations_and_activation: { enabled: true },
        program_activity_and_automation: { enabled: true },
        program_reports: { enabled: true }
      },
      platform: {
        enabled: true,
        user_management: { enabled: true },
        platform_security_alerts: { enabled: true }
      },
      security: { enabled: true }
    };

    // Deep normalization to merge incoming data with defaults while strictly preserving FALSE values
    const normalize = (data: any) => {
      if (!data) return defaults;

      // If data is a string (legacy/corruption), try to parse it
      let parsed = data;
      if (typeof data === 'string') {
        try {
          parsed = JSON.parse(data);
        } catch (e) {
          return defaults;
        }
      }

      const deepMerge = (target: any, source: any) => {
        const result = { ...source };
        if (!target) return result;

        Object.keys(source).forEach(key => {
          if (target[key] !== undefined) {
            if (typeof source[key] === 'object' && source[key] !== null && !Array.isArray(source[key])) {
              result[key] = deepMerge(target[key], source[key]);
            } else {
              result[key] = target[key];
            }
          }
        });
        return result;
      };

      return deepMerge(parsed, defaults);
    };

    setPrefs(normalize(email_preferences));
  }, [email_preferences]);

  const saveSettings = async () => {
    if (!username || !prefs) return;
    const loader = toast.loading("Saving preferences...");
    try {
      await setDetails({ username, email_preferences: prefs }, true);
      toast.success("Preferences saved successfully");
    } catch (err) {
      toast.error("Failed to save preferences");
    } finally {
      toast.remove(loader);
    }
  };

  const updatePrefs = async (path: string, val: any) => {
    if (!prefs || !username) return;

    const newPrefs = JSON.parse(JSON.stringify(prefs));
    const parts = path.split('.');
    let current = newPrefs;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) current[parts[i]] = {};
      current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = val;

    // [New] Hierarchical Parent-Child Sync Logic
    const sectionKey = parts[0];
    const section = newPrefs[sectionKey];

    if (parts.length === 2 && parts[1] === 'enabled') {
      // MASTER TOGGLE CHANGE (Section Level): Cascade down to all sub-modules
      Object.keys(section).forEach(key => {
        if (key !== 'enabled' && typeof section[key] === 'object' && section[key] !== null) {
          if ('enabled' in section[key]) {
            section[key].enabled = val;
            // Also cascade to severities if they exist
            if (section[key].severities) {
              Object.keys(section[key].severities).forEach(s => {
                section[key].severities[s] = val;
              });
            }
          }
        }
      });
    } else {
      // CHILD CHANGE: Bubble up to parents
      
      // 1. If a severity changed, update the sub-module's 'enabled' status
      if (path.includes('.severities.')) {
        const subModuleKey = parts[1];
        const subModule = section[subModuleKey];
        if (subModule && subModule.severities) {
          const isAnySeverityEnabled = Object.values(subModule.severities).some(Boolean);
          subModule.enabled = isAnySeverityEnabled;
        }
      }

      // 2. If any sub-module's 'enabled' status changed, update the Section status
      if (section) {
        // Define which sub-modules are visible and should be considered for the parent toggle
        let visibleSubModules: string[] = [];
        if (sectionKey === 'reports') {
          visibleSubModules = ['vulnerability_reports', 'comments'];
          if (isResearcher || isAdmin) visibleSubModules.push('retests');
        } else if (sectionKey === 'programs') {
          visibleSubModules = ['invitations_and_activation', 'program_activity_and_automation'];
        } else if (sectionKey === 'platform') {
          visibleSubModules = ['user_management', 'platform_security_alerts', 'assets'];
        }

        const isAnyVisibleSubModuleEnabled = visibleSubModules.some(key => {
          const sub = section[key];
          return sub && typeof sub === 'object' && sub.enabled === true;
        });

        section.enabled = isAnyVisibleSubModuleEnabled;
        
        // Also ensure hidden sub-modules follow the parent if they aren't visible
        // (This prevents hidden "true" values from keeping a module "alive" in the backend)
        Object.keys(section).forEach(key => {
          if (key !== 'enabled' && !visibleSubModules.includes(key)) {
             if (typeof section[key] === 'object' && section[key] !== null && 'enabled' in section[key]) {
               section[key].enabled = isAnyVisibleSubModuleEnabled;
             }
          }
        });
      }
    }

    // Update local state for instant UI feedback
    setPrefs(newPrefs);

    // Auto-save to database in the background
    try {
      await setDetails({ username, email_preferences: newPrefs }, false);
    } catch (err) {
      console.error("Auto-save notification preferences failed:", err);
    }
  };

  if (!prefs) return null;

  // Determine which sections to show based on Role
  const isResearcher = userRole === 1;
  const isAdmin = [3, 4, 5, 9].includes(userRole as any);
  const isSimplifiedRole = [6, 7].includes(userRole as any);

  if (userRole === UserRole.SOLUTIONS_SPECIALIST) {
    return (
      <div className={`flex flex-col max-w-full gap-6 ${className || ""}`}>
        {/* Detached Box for Global Email Settings */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Global Email Settings</h1>
            <p className="text-sm text-gray-500 mt-1">
              Turning this off will stop all non-essential platform emails.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-gray-700">
              {email_notifications_enabled !== false ? "Enabled" : "Disabled"}
            </span>
            <div className="scale-110">
              <Toggle 
                value={email_notifications_enabled !== false} 
                onChange={(val: boolean) => username && setDetails({ username, email_notifications_enabled: val }, true)} 
                activeTrackColor="#0D1117"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col max-w-full gap-6 ${className || ""}`}>
      {/* HEADER SECTION (Detached Box) */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Email Preferences</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isSimplifiedRole
              ? "Manage your global communication settings."
              : `Customize which notifications you receive as a ${isResearcher ? "Researcher" : isAdmin ? "Admin" : "Business Owner"}.`
            }
          </p>
        </div>
        {!isSimplifiedRole && (
          <button
            onClick={saveSettings}
            className="bg-[#0D1117] text-white px-6 py-2.5 rounded-lg font-bold text-sm hover:bg-black transition-colors"
          >
            Save Preferences
          </button>
        )}
      </div>

      {/* GRANULAR SECTIONS */}
      {!isSimplifiedRole && (
        <>
          {/* REPORTS SECTION */}
          <PreferenceSection
            title="Reports"
            description={isResearcher ? "Manage notifications for your submitted vulnerabilities and feedback." : "Monitor report traffic, triage events, and program health."}
            icon={<ReportIcon />}
            enabled={prefs?.reports?.enabled}
            onToggle={(val: boolean) => updatePrefs('reports.enabled', val)}
            isGlobalOff={email_notifications_enabled === false}
          >
            <PreferenceRow
              label={isResearcher ? "Report & Retest Updates" : isAdmin ? "Report Lifecycle & SLA" : "Report Lifecycle"}
              title={isResearcher ? "Vulnerability & Retest Status" : isAdmin ? "Submissions, Retests & SLA Alerts" : "Submissions & Status Updates"}
              description={isResearcher ? "Notify when your report/retest status changes or an update is made." : isAdmin ? "Notify when reports/retests are submitted, updated, or breach SLAs." : "Notify when reports/retests are submitted, updated, or require review."}
              icon={<MailIcon />}
              enabled={prefs?.reports?.vulnerability_reports?.enabled}
              onToggle={(val: boolean) => updatePrefs('reports.vulnerability_reports.enabled', val)}
              isGlobalOff={email_notifications_enabled === false}
              extra={<SeverityDropdown
                selected={prefs?.reports?.vulnerability_reports?.severities || {}}
                onChange={(sev: string, val: boolean) => updatePrefs(`reports.vulnerability_reports.severities.${sev}`, val)}
                disabled={email_notifications_enabled === false || !prefs?.reports?.vulnerability_reports?.enabled}
              />}
            />
            <PreferenceRow
              label="Collaboration"
              title={isResearcher ? "New Comments on My Reports" : "Communication Alerts"}
              description={isResearcher ? "Notify when a researcher or admin comments on your submission." : "Notify when a new comment or internal message is posted on a report."}
              icon={<CommentIcon />}
              enabled={prefs?.reports?.comments?.enabled}
              onToggle={(val: boolean) => updatePrefs('reports.comments.enabled', val)}
              isGlobalOff={email_notifications_enabled === false}
            />
          </PreferenceSection>

          {/* PROGRAMS SECTION */}
          <PreferenceSection
            title="Programs"
            description="Stay updated on program launches, invitations, and scope changes."
            icon={<ProgramIcon />}
            enabled={prefs?.programs?.enabled}
            onToggle={(val: boolean) => updatePrefs('programs.enabled', val)}
            isGlobalOff={email_notifications_enabled === false}
          >
            <PreferenceRow
              label="Program Status"
              title={isResearcher ? "Private Program Invitations" : "Status & Access Control"}
              description={isResearcher ? "Notify when you are invited to join a private security program." : "Notify for manual program activations, deactivations, and status changes."}
              icon={<div className="bg-[#58CC02]/[0.17] p-2 rounded-lg"><svg className="w-5 h-5 text-[#58CC02]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg></div>}
              enabled={prefs?.programs?.invitations_and_activation?.enabled}
              onToggle={(val: boolean) => updatePrefs('programs.invitations_and_activation.enabled', val)}
              isGlobalOff={email_notifications_enabled === false}
            />
            <PreferenceRow
              label="Activity"
              title="Lifecycle & Operational Alerts"
              description={isResearcher ? "Notify for upcoming program launches and assignment changes." : "Notify for scheduled launches, scope verification updates, and automated system reminders."}
              icon={<div className="bg-[#58CC02]/[0.17] p-2 rounded-lg"><svg className="w-5 h-5 text-[#58CC02]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg></div>}
              enabled={prefs?.programs?.program_activity_and_automation?.enabled}
              onToggle={(val: boolean) => updatePrefs('programs.program_activity_and_automation.enabled', val)}
              isGlobalOff={email_notifications_enabled === false}
            />
          </PreferenceSection>

          {/* ADMIN & SECURITY SECTION */}
          {isAdmin && (
            <PreferenceSection
              title="Admin & Security"
              description="System-wide management alerts for users and platform security."
              icon={<UserIcon />}
              enabled={prefs?.platform?.enabled}
              onToggle={(val: boolean) => updatePrefs('platform.enabled', val)}
              isGlobalOff={email_notifications_enabled === false}
            >
              <PreferenceRow
                label="User Management"
                title="New User Registrations"
                description="Notify when new users sign up or require account approval by an admin."
                icon={<UserIcon />}
                enabled={prefs?.platform?.user_management?.enabled}
                onToggle={(val: boolean) => updatePrefs('platform.user_management.enabled', val)}
                isGlobalOff={email_notifications_enabled === false}
              />
              <PreferenceRow
                label="Security"
                title="Vault & Access Alerts"
                description="Critical alerts for vault credential changes and access anomalies."
                icon={<div className="bg-[#58CC02]/[0.17] p-2 rounded-lg"><svg className="w-5 h-5 text-[#58CC02]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg></div>}
                enabled={prefs?.platform?.platform_security_alerts?.enabled}
                onToggle={(val: boolean) => updatePrefs('platform.platform_security_alerts.enabled', val)}
                isGlobalOff={email_notifications_enabled === false}
              />
              <PreferenceRow
                label="Discovery"
                title="New Asset Alerts"
                description="Notify when new assets are discovered or added to the organization scope."
                icon={<div className="bg-[#58CC02]/[0.17] p-2 rounded-lg"><svg className="w-5 h-5 text-[#58CC02]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg></div>}
                enabled={prefs?.platform?.assets?.enabled}
                onToggle={(val: boolean) => updatePrefs('platform.assets.enabled', val)}
                isGlobalOff={email_notifications_enabled === false}
              />
            </PreferenceSection>
          )}
        </>
      )}
    </div>
  );
};

export default NotificationSettings;
